import type { ErrorItem } from "@auth0/auth0-acul-react/types";

/**
 * Auth0 codes seen (or likely) after reset-password submit / `resendEmail()`.
 * Official React SDK docs do not enumerate them — they only expose
 * `useErrors()` / `getErrors()`. Add codes here as the tenant confirms them.
 *
 * Classify only. Never render `error.message`: Auth0 copy can leak account
 * hints and uses formal *Sie*, which does not match our catalog.
 */
const RATE_LIMIT_ERROR_CODES = new Set([
  "too_many_emails",
  "too_many_attempts",
  "too_many_requests",
]);

export type Auth0ErrorKind = "rate-limit" | "generic";

function getErrorCode(error: ErrorItem): string {
  return "code" in error ? String(error.code) : "";
}

export function isAuth0RateLimitError(error: ErrorItem): boolean {
  if (RATE_LIMIT_ERROR_CODES.has(getErrorCode(error))) {
    return true;
  }

  const message = error.message.toLowerCase();

  return (
    message.includes("too many emails") ||
    message.includes("too many attempts") ||
    message.includes("too many requests") ||
    message.includes("zu viele versuche")
  );
}

export function resolveAuth0ErrorKind({
  auth0Errors,
  hasAuth0Error,
  hasSubmitRejection,
}: {
  auth0Errors: readonly ErrorItem[];
  hasAuth0Error: boolean;
  hasSubmitRejection: boolean;
}): Auth0ErrorKind | null {
  if (auth0Errors.some(isAuth0RateLimitError)) {
    return "rate-limit";
  }

  if (hasAuth0Error || hasSubmitRejection || auth0Errors.length > 0) {
    return "generic";
  }

  return null;
}
